import React, { useState } from 'react';
import { Head, useForm } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { AlertCircle } from 'lucide-react';
import LegalConsentModal from '../../components/LegalConsentModal';

interface ConsentRequiredProps {
  currentVersions: {
    terms: string;
    privacy: string;
  };
  userAgreedVersions: {
    terms: string | null;
    privacy: string | null;
  };
}

const ConsentRequired: React.FC<ConsentRequiredProps> = ({
  currentVersions,
  userAgreedVersions,
}) => {
  const [showConsentModal, setShowConsentModal] = useState(true);
  const { post, processing } = useForm({});

  function handleConsentAgree() {
    post('/consent-required');
    setShowConsentModal(false);
  }

  function handleConsentDisagree() {
    // User must agree to continue, so we just close and re-open
    // In practice, they can't proceed without agreeing
    setShowConsentModal(true);
  }

  const hasVersionChange =
    userAgreedVersions.terms !== currentVersions.terms ||
    userAgreedVersions.privacy !== currentVersions.privacy;

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <Head title="Consent Required">
        <meta property="og:title" content="Consent Required" />
        <meta
          property="og:description"
          content="Please review and agree to our updated policies"
        />
        <meta property="og:image" content="https://hd.supavp.com/og-image.svg" />
        <meta property="og:url" content="https://hd.supavp.com/consent-required" />
        <meta property="og:type" content="website" />
      </Head>

      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-full">
              <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Policy Update Required
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Our Terms of Service and/or Privacy Policy have been updated. Please
            review and agree to continue using our service.
          </p>
        </div>

        {/* Version Info Card */}
        <Card className="p-5 sm:p-6 md:p-8 mb-6">
          <h2 className="font-semibold mb-4">What changed?</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Terms of Service</span>
              <div className="text-right">
                <span className="text-xs text-muted-foreground">You agreed to:</span>
                <p className="text-sm font-medium">
                  v{userAgreedVersions.terms || 'None'}
                </p>
                <span className="text-xs text-green-600">Current: v{currentVersions.terms}</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Privacy Policy</span>
              <div className="text-right">
                <span className="text-xs text-muted-foreground">You agreed to:</span>
                <p className="text-sm font-medium">
                  v{userAgreedVersions.privacy || 'None'}
                </p>
                <span className="text-xs text-green-600">Current: v{currentVersions.privacy}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Button
            onClick={() => setShowConsentModal(true)}
            className="w-full"
            size="lg"
          >
            Review & Agree to Updated Policies
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            You must agree to continue using the service
          </p>
        </div>
      </div>

      {/* Consent Modal */}
      <LegalConsentModal
        isOpen={showConsentModal}
        onAgree={handleConsentAgree}
        onDisagree={handleConsentDisagree}
        isReconsent={true}
      />
    </div>
  );
};

export default ConsentRequired;
